import React, { useState, useEffect } from 'react';
import API_BASE_URL from '../../../components/apiconfig/api-config';
import { useNavigate } from 'react-router-dom';
import { FaPlus, FaSearch } from 'react-icons/fa';
import NoRecordFound from '../../../components/common/no-record-found';

const IssuingAuthoritiesList = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [totalRecords, setTotalRecords] = useState(0);
  const navigate = useNavigate();

  const fetchData = async (page = 1, search = '') => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '10',
        ...(search && { search })
      });

      const response = await fetch(`${API_BASE_URL}/api/masters/issuing-authorities-get?${params}`, {
        credentials: 'include'
      });

      if (response.ok) {
        const result = await response.json();
        setData(result.data || []);
        setTotalRecords(result.total || 0);
        setTotalPages(Math.ceil((result.total || 0) / 10));
      } else {
        console.error('Failed to fetch data');
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(currentPage, searchTerm);
  }, [currentPage, searchTerm]);

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchData(1, searchTerm);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-IN');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#10a5e9]"></div>
      </div>
    );
  }

  return (
    <div className="max-w-8xl mx-auto p-0 bg-white">
      <div className="flex justify-between items-center border-b border-gray-100 bg-gray-50 px-4 py-1.5">
        <h2 className="text-lg font-bold mb-0 text-left">Issuing Authorities</h2>
        <button
          onClick={() => navigate('/issuing-authorities/add')}
          className="flex items-center gap-1 bg-[#3899cf] text-white px-3 py-1 rounded text-xs font-medium hover:bg-[#0e8dc6]"
        >
          <FaPlus />
          Add New
        </button>
      </div>

      <div className="px-4 py-3">
        <form onSubmit={handleSearch} className="mb-4">
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <input
                type="text"
                placeholder="Search by authority name, designation or status..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full border border-gray-300 px-3 py-2 pl-10 text-sm focus:outline-none focus:border-[#10a5e9] rounded-none"
              />
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm" />
            </div>
            <button
              type="submit"
              className="bg-[#3899cf] text-white px-4 py-2 text-sm font-medium hover:bg-[#0e8dc6] rounded-none"
            >
              Search
            </button>
          </div>
        </form>

        {data.length === 0 ? (
          <NoRecordFound />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left py-2 px-3 font-medium text-gray-700">ID</th>
                    <th className="text-left py-2 px-3 font-medium text-gray-700">Authority Name</th>
                    <th className="text-left py-2 px-3 font-medium text-gray-700">Designation</th>
                    <th className="text-left py-2 px-3 font-medium text-gray-700">Signature Image</th>
                    <th className="text-left py-2 px-3 font-medium text-gray-700">Status</th>
                    <th className="text-left py-2 px-3 font-medium text-gray-700">Created At</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((item, index) => (
                    <tr key={item.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-2 px-3">{item.id}</td>
                      <td className="py-2 px-3 font-medium">{item.authority_name}</td>
                      <td className="py-2 px-3">
                        {item.designation ? (
                          <span className="px-2 py-1 text-xs rounded bg-blue-100 text-[#3899cf]">
                            {item.designation}
                          </span>
                        ) : (
                          <span className="text-gray-400">N/A</span>
                        )}
                      </td>
                      <td className="py-2 px-3">
                        {item.signature_image ? (
                          <div className="flex items-center gap-2">
                            <img 
                              src={`${API_BASE_URL}/uploads/${item.signature_image}`} 
                              alt="Signature" 
                              className="w-12 h-12 object-cover border border-gray-300 rounded"
                            />
                            <a 
                              href={`${API_BASE_URL}/uploads/${item.signature_image}`} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-[#3899cf] hover:underline text-xs"
                            >
                              View Full
                            </a>
                          </div>
                        ) : (
                          <span className="text-gray-400">No image</span>
                        )}
                      </td>
                      <td className="py-2 px-3">
                        <span className={`px-2 py-1 text-xs rounded ${
                          item.authority_Status === 'Active' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {item.authority_Status}
                        </span>
                      </td>
                      <td className="py-2 px-3 text-gray-600">{formatDate(item.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="flex justify-between items-center mt-4">
                <div className="text-sm text-gray-600">
                  Showing {((currentPage - 1) * 10) + 1} to {Math.min(currentPage * 10, totalRecords)} of {totalRecords} entries
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-3 py-1 text-sm border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Previous
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`px-3 py-1 text-sm border ${
                        currentPage === page
                          ? 'bg-[#3899cf] text-white border-[#3899cf]'
                          : 'border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 text-sm border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default IssuingAuthoritiesList; 